import type { FC, ReactNode } from "react";
import * as RadixCheckbox from "@radix-ui/react-checkbox";
import { CheckIcon } from "@heroicons/react/16/solid";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";

const checkboxVariants = cva(
  "flex shrink-0 cursor-pointer items-center justify-center border border-neutral-mid-grey bg-neutral-white transition-colors data-[state=checked]:border-brand-blue data-[state=checked]:bg-brand-blue disabled:cursor-not-allowed disabled:opacity-30 disabled:pointer-events-none",
  {
    variants: {
      size: {
        m: "size-16 rounded-[3px]",
        l: "size-22 rounded-[4px]",
      },
    },
    defaultVariants: {
      size: "m",
    },
  },
);

const checkIconSize = {
  m: "size-11",
  l: "size-16",
} as const;

type CheckboxProps = Omit<RadixCheckbox.CheckboxProps, "asChild"> &
  VariantProps<typeof checkboxVariants> & {
    label?: ReactNode;
  };

export const Checkbox: FC<CheckboxProps> = ({ className, size = "m", label, ...props }) => {
  const box = (
    <RadixCheckbox.Root className={cn(checkboxVariants({ size }), className)} {...props}>
      <RadixCheckbox.Indicator>
        <CheckIcon className={cn(checkIconSize[size ?? "m"], "text-neutral-white")} />
      </RadixCheckbox.Indicator>
    </RadixCheckbox.Root>
  );

  if (!label) {
    return box;
  }

  return (
    <label className="inline-flex cursor-pointer items-center gap-6 text-16 text-neutral-black">
      {box}
      {label}
    </label>
  );
};
